import GameObject from '../../../GameObject/GameObject';
import AnimationClip from '../AnimationClip';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import { delay } from '../../../Utility/Task/TaskQueue';
import { StartAnimation, StartAnimationClip } from '../AnimationHelper';
import TransformComponent from '../../../Component/Transform';
import CardComponent from '../../../Component/Level/Card';
import { Easing } from '@tweenjs/tween.js';
import { Random } from '../../../Utility/Random';
import Renderable from '../../../Component/Renderable';
import Point from '../../../Utility/Point';
import CardSystem from '../../Card/CardSystem';
import { UIEvent } from '../../UI/BaseUISystem';
import DragonbonesComponent from 'Component/Dragonbones';
import { ModifierTypeMap } from 'Prototype/Level/Card/ModifierTypeMap';
import { CardModifierType } from 'Prototype/Level/Card/CardModifierType';
import { DragonbonesDefault } from './DragonbonesDefault';
import { Alpha } from './Alpha';

interface ICardBombExplosionMeta {
    system: CardSystem;
    sourceCard: BaseCard;
    targetCards: BaseCard[];
}

export class CardBombExplosion extends AnimationClip<ICardBombExplosionMeta> {
    public *execute(animator: GameObject, meta: ICardBombExplosionMeta): IterableIterator<number> {
        animator.addChild(meta.sourceCard);
        const cardFront = meta.sourceCard.findChildByName(BaseCard.CARD_FRONT_NAME);
        const bombModifier = meta.sourceCard.getModifier(ModifierTypeMap[CardModifierType.bomb].prototype);
        const dragonbonesComponent = bombModifier.getComponentsInChildren(DragonbonesComponent)[0];
        const armature = dragonbonesComponent.gameObject;

        StartAnimationClip(new Alpha(cardFront, { startAlpha: 1, endAlpha: 0, duration: 150, delay: 50 }));
        StartAnimationClip(
            new DragonbonesDefault(armature, {
                animationName: 'explosion',
                playTimes: 1,
            })
        );

        const durationIn = 400;
        const durationOut = 900;

        yield* delay(durationIn);
        meta.system.cardsContainer.addChild(meta.sourceCard);

        this.backgroundShake();

        const sourceCardTransform = meta.sourceCard.getComponent(TransformComponent);

        // Sort based on depth so cards are never added in the wrong overlapping order.
        meta.targetCards.sort((a, b) => a.getComponent(CardComponent).depth - b.getComponent(CardComponent).depth);

        for (let targetCard of meta.targetCards) {
            meta.system.cardsContainer.addChild(targetCard);
            targetCard.getComponent(Renderable).visible = true;
            const transform = targetCard.getComponent(TransformComponent);
            const cardComponent = targetCard.getComponent(CardComponent);
            const tweenX = this.getTween(transform.position);
            const tweenY1 = this.getTween(transform.position);
            const tweenY2 = this.getTween(transform.position);
            const tweenRotation = this.getTween(transform);

            transform.position.copy(sourceCardTransform.position as Point);
            transform.rotation = cardComponent.rotation + (4 + Random.int(-1, 1)) * Math.PI * 2;

            tweenX
                .to({ x: cardComponent.x }, durationOut)
                .easing(Easing.Quadratic.Out)
                .start(this.elapsedTime);
            tweenY1
                .to({ y: cardComponent.y - 200 }, durationOut * 0.5)
                .easing(Easing.Quadratic.Out)
                .chain(tweenY2.to({ y: cardComponent.y }, durationOut * 0.5).easing(Easing.Quadratic.In))
                .start(this.elapsedTime);
            tweenRotation
                .to({ rotation: cardComponent.rotation }, durationOut)
                .easing(Easing.Cubic.Out)
                .start(this.elapsedTime);
        }

        yield* delay(durationOut);
        meta.system.sortCardsInPlay(meta.system.store.getState().levelState.cardsState);
        meta.system.scene.emit(UIEvent.RESTORE_INTERACTIVITY, true);
    }

    private backgroundShake() {
        const background = GameObject.findByName('backgroundSprite')[0];
        if (background) {
            StartAnimation(background, 'LevelScreenShake', {
                amplitude: 15,
                frequency: 25,
                damping: 0.9,
            });
        }
    }
}
